/* global globalThis */
/* Canonical shared scoring engine. Native copy is generated by sync_native.py. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports)
    module.exports = factory(require('../../../utils/csvCell.ts').csvCell);
  else root.FlashCore = factory(root.csvCell);
})(typeof globalThis !== 'undefined' ? globalThis : this, function (csvCell) {
  'use strict';
  const DAY = 86400000,
    finite = (x) => typeof x === 'number' && Number.isFinite(x),
    dateOK = (s) =>
      typeof s === 'string' &&
      /^\d{4}-\d{2}-\d{2}$/.test(s) &&
      !Number.isNaN(Date.parse(s + 'T00:00:00Z')) &&
      new Date(s + 'T00:00:00Z').toISOString().slice(0, 10) === s;
  const days = (a, b) => Math.round((Date.parse(b + 'T00:00:00Z') - Date.parse(a + 'T00:00:00Z')) / DAY) + 1;
  const add = (a, n) => new Date(Date.parse(a + 'T00:00:00Z') + n * DAY).toISOString().slice(0, 10);
  const MAX_WEEKLY_EXCLUSIONS = 50;
  const DEFAULTS = {
    view: 'portfolio',
    rollup: 'campaign',
    sort: 'gap',
    comparability: 'comparable',
    trajectoryMeasure: 'packages',
    historyView: 'previous',
    weeklyExclusion: '',
    campaignComparison: 'previous',
    status: 'live',
    country: '',
    destination: '',
    owner: '',
    pcm: '',
    territory: '',
    reviewBy: '',
    reviewGroup: '',
    tab: 'overview',
    campaignType: '',
    launchFrom: '',
    launchTo: '',
    observationFrom: '',
    observationTo: '',
    ageMin: '',
    ageMax: '',
    search: '',
    campaign: '',
    cohort: 'primary',
    peerScope: 'destination',
    minN: '5',
    market: '',
  };
  function exclusionIds(value = '') {
    return [
      ...new Set(
        String(value)
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean),
      ),
    ].sort();
  }
  function filters(input) {
    const out = { ...DEFAULTS };
    for (const k of Object.keys(out)) if (input[k] !== undefined) out[k] = String(input[k]);
    if (out.comparability === 'uncompared') out.comparability = 'uncovered';
    if (!['portfolio', 'campaign', 'campaigns'].includes(out.view)) out.view = 'portfolio';
    if (!['campaign', 'territory', 'pcm'].includes(out.rollup)) out.rollup = 'campaign';
    if (!['gap', 'margin', 'name', 'age'].includes(out.sort)) out.sort = 'gap';
    if (!['all', 'comparable', 'uncovered'].includes(out.comparability)) out.comparability = 'comparable';
    if (!['packages', 'margin'].includes(out.trajectoryMeasure)) out.trajectoryMeasure = 'packages';
    if (!['previous', 'weekly'].includes(out.historyView)) out.historyView = 'previous';
    out.weeklyExclusion = exclusionIds(out.weeklyExclusion).join(',');
    if (!['previous', 'peers'].includes(out.campaignComparison)) out.campaignComparison = 'previous';
    if (!['overview', 'funnel', 'campaigns', 'comparison'].includes(out.tab)) out.tab = 'overview';
    if (!['live', 'completed', 'all'].includes(out.status)) out.status = 'live';
    if (!['primary', 'secondary'].includes(out.cohort)) out.cohort = 'primary';
    if (!['destination', 'country', 'global'].includes(out.peerScope)) out.peerScope = 'destination';
    if (!['3', '5', '10'].includes(out.minN)) out.minN = '5';
    return out;
  }
  function fromSearch(search) {
    const q = new URLSearchParams(search),
      f = {};
    for (const k of Object.keys(DEFAULTS)) if (q.has(k)) f[k] = q.get(k);
    if (q.has('flashView')) f.view = q.get('flashView');
    if (!f.campaign) f.campaign = q.get('flashCampaign') || '';
    if (!f.cohort) f.cohort = q.get('flashCohort') || 'primary';
    if (!f.view)
      f.view =
        f.tab === 'campaigns'
          ? 'campaigns'
          : ['funnel', 'comparison'].includes(f.tab) || f.campaign
            ? 'campaign'
            : 'portfolio';
    // Compatibility for saved links from the original single-campaign switch.
    if (f.weeklyExclusion === 'paradisus') f.weeklyExclusion = '006RF00000ZlcyYYAR:2026-09-28';
    return filters(f);
  }
  function toSearch(f, existing = '') {
    const q = new URLSearchParams(existing);
    q.delete('flashCampaign');
    q.delete('flashCohort');
    q.delete('view');
    for (const k of Object.keys(DEFAULTS)) {
      const key = k === 'view' ? 'flashView' : k;
      if (f[k] && f[k] !== DEFAULTS[k]) q.set(key, f[k]);
      else q.delete(key);
    }
    if (f.view === 'portfolio' && (f.campaign || f.tab !== DEFAULTS.tab)) q.set('flashView', 'portfolio');
    return q;
  }
  function errors(f, cutoff) {
    const e = [];
    if (!['', 'pcm', 'territory'].includes(f.reviewBy)) e.push('Choose PCM or territory for group review.');
    if (Boolean(f.reviewBy) !== Boolean(f.reviewGroup)) e.push('Choose both the review grouping and group.');
    for (const k of ['launchFrom', 'launchTo', 'observationFrom', 'observationTo'])
      if (f[k] && !dateOK(f[k])) e.push('Enter a valid ' + k + ' date.');
    if (f.launchFrom && f.launchTo && f.launchFrom > f.launchTo)
      e.push('Launch start must be on or before launch end.');
    if (f.observationFrom && f.observationTo && f.observationFrom > f.observationTo)
      e.push('Observation start must be on or before observation end.');
    for (const k of ['ageMin', 'ageMax'])
      if (f[k] && (!/^\d+$/.test(f[k]) || Number(f[k]) < 1))
        e.push('Campaign age must be a whole number of at least one day.');
    if (f.ageMin && f.ageMax && Number(f.ageMin) > Number(f.ageMax)) e.push('Minimum age must not exceed maximum age.');
    if (f.observationFrom && f.observationFrom > cutoff) e.push('Observation start is after the data cutoff.');
    return e;
  }
  function measure(c, f, cutoff, age) {
    const first = age ? c.startDate : [c.startDate, f.observationFrom || c.startDate].sort().at(-1),
      last = [c.endDate, cutoff, f.observationTo || cutoff, age ? add(c.startDate, age - 1) : cutoff].sort()[0];
    const n = last >= first ? days(first, last) : 0;
    const t = { impressions: 0, views: 0, packages: 0, margin: 0 },
      coverage = { impressions: 0, views: 0, sales: 0 };
    const facts = f.market ? c.dailyByMarket?.[f.market] || [] : c.daily || [];
    for (const d of facts) {
      if (d.date < first || d.date > last) continue;
      for (const k of Object.keys(t)) if (finite(d[k])) t[k] += d[k];
      if (!f.market && d.impressionObserved === true) coverage.impressions++;
      if (d.viewsObserved === true) coverage.views++;
      if (d.salesObserved === true) coverage.sales++;
    }
    const impressions = n && coverage.impressions > 0 ? t.impressions : null,
      views = n && coverage.views > 0 ? t.views : null,
      packages = n && coverage.sales > 0 ? t.packages : null,
      margin = n && coverage.sales > 0 ? t.margin : null;
    const ratio = (a, b) => (finite(a) && finite(b) && b > 0 ? a / b : null);
    return {
      ...c,
      observedStart: first,
      observationEnd: last,
      daysObserved: n,
      totals: { impressions, views, packages, margin },
      coverage: { ...coverage, calendarDays: n },
      metrics: {
        impressions_per_day: ratio(impressions, n),
        views_per_day: ratio(views, n),
        impressions_to_views: ratio(views, impressions),
        packages_per_day: ratio(packages, n),
        views_to_packages: ratio(packages, views),
        margin_per_day: ratio(margin, n),
        margin_per_view: ratio(margin, views),
      },
    };
  }
  function quantile(a, p) {
    if (!a.length) return null;
    const ix = (a.length - 1) * p,
      lo = Math.floor(ix);
    return a[lo] + (a[Math.ceil(ix)] - a[lo]) * (ix - lo);
  }
  function stats(values) {
    const a = values.filter(finite).sort((x, y) => x - y),
      n = a.length,
      mean = n ? a.reduce((x, y) => x + y, 0) / n : null,
      sd = n > 1 ? Math.sqrt(a.reduce((s, v) => s + (v - mean) ** 2, 0) / (n - 1)) : null;
    return {
      n,
      mean,
      sd,
      q1: quantile(a, 0.25),
      median: quantile(a, 0.5),
      q3: quantile(a, 0.75),
      lower1sd: finite(mean) && finite(sd) ? mean - sd : null,
      upper1sd: finite(mean) && finite(sd) ? mean + sd : null,
    };
  }
  function prepare(pool, id) {
    const sorted = pool
        .filter((c) => finite(c.metrics[id]))
        .map((c) => ({ id: c.id, value: c.metrics[id] }))
        .sort((a, b) => a.value - b.value),
      positions = new Map(sorted.map((r, i) => [r.id, i]));
    const full = stats(sorted.map((r) => r.value));
    return { sorted, positions, full };
  }
  function without(p, id) {
    const ix = p.positions.get(id);
    if (ix === undefined) return p.full;
    const old = p.full,
      n = old.n - 1;
    if (!n) return stats([]);
    const value = p.sorted[ix].value,
      mean = (old.mean * old.n - value) / n,
      originalM2 = old.sd === null ? 0 : old.sd ** 2 * (old.n - 1),
      residualM2 = originalM2 - (value - old.mean) * (value - mean),
      m2 = Math.max(0, residualM2);
    // Removing a dominant outlier can cancel the remaining variance. Recompute only that unstable case.
    if (originalM2 > 0 && Math.abs(residualM2) <= originalM2 * 1e-10)
      return stats(p.sorted.filter((_, i) => i !== ix).map((r) => r.value));
    const at = (i) => p.sorted[i >= ix ? i + 1 : i].value,
      q = (rate) => {
        const pos = (n - 1) * rate,
          lo = Math.floor(pos);
        return at(lo) + (at(Math.ceil(pos)) - at(lo)) * (pos - lo);
      };
    return {
      n,
      mean,
      sd: n > 1 ? Math.sqrt(m2 / (n - 1)) : null,
      q1: q(0.25),
      median: q(0.5),
      q3: q(0.75),
      lower1sd: n > 1 ? mean - Math.sqrt(m2 / (n - 1)) : null,
      upper1sd: n > 1 ? mean + Math.sqrt(m2 / (n - 1)) : null,
    };
  }
  function compare(value, s, minN) {
    const valid = finite(value) && s.n >= minN && finite(s.mean) && finite(s.sd) && s.sd > 0,
      z = valid ? (value - s.mean) / s.sd : null;
    return {
      ...s,
      value,
      z,
      positionQuartile: z === null ? 'unscored' : value < s.q1 ? 'below' : value > s.q3 ? 'above' : 'in_line',
      classification: z === null ? 'unscorable' : z < -0.5 ? 'below' : z > 0.5 ? 'above' : 'within',
      reason: !finite(value)
        ? 'Value unavailable'
        : s.n < minN
          ? 'Fewer than ' + minN + ' comparators'
          : !finite(s.sd) || s.sd === 0
            ? 'No measurable variation'
            : '',
    };
  }
  function dimensions(c, f) {
    return (
      (!f.country || c.country === f.country) &&
      (!f.destination || c.destination === f.destination) &&
      (!f.owner || c.owner === f.owner) &&
      (!f.pcm || (c.pcmName || 'No current-quarter PCM record') === f.pcm) &&
      (!f.territory || (c.strategicTerritory || 'Unknown strategic territory') === f.territory) &&
      (!f.campaignType || c.campaignType === f.campaignType) &&
      (!f.launchFrom || c.startDate >= f.launchFrom) &&
      (!f.launchTo || c.startDate <= f.launchTo)
    );
  }
  function peerKey(c, f) {
    const unknown = (v) => !v || /^unknown|^unassigned/i.test(v);
    const type = c.campaignType || 'unknown-type:' + c.id;
    if (f.peerScope === 'global') return 'global|' + type;
    if (unknown(c.country) || (f.peerScope === 'destination' && unknown(c.destination))) return 'unknown:' + c.id;
    return (f.peerScope === 'country' ? c.country : c.country + '|' + c.destination) + '|' + type;
  }
  function derive(snapshot, input) {
    const f = filters(input),
      cutoff = snapshot.meta.observationCutoff || snapshot.meta.asOf,
      invalid = errors(f, cutoff),
      definitions = snapshot.metricDefinitions || [];
    if (
      f.market &&
      (!snapshot.sourceCapabilities?.market?.supported ||
        !snapshot.sourceCapabilities.market.values?.includes(f.market))
    )
      invalid.push(
        'This website market is not supported by the snapshot. Clear filters or choose an available market.',
      );
    if (invalid.length)
      return {
        filters: f,
        errors: invalid,
        queue: [],
        selected: null,
        comparators: [],
        metrics: {},
        ageSensitivity: null,
        scope: { cutoff },
      };
    const universe = snapshot.campaigns.filter((c) => dimensions(c, f)).map((c) => measure(c, f, cutoff));
    const peers = universe.filter(
      (c) =>
        c.status === 'completed' &&
        c.isMature !== false &&
        c.endDate <= cutoff &&
        c.daysObserved > 0 &&
        (f.cohort === 'secondary' || (c.durationDays >= 28 && c.durationDays <= 42)),
    );
    const groups = new Map(),
      cache = new Map();
    for (const c of peers) {
      const key = peerKey(c, f);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(c);
    }
    function comparisons(c) {
      const key = peerKey(c, f),
        pool = groups.get(key) || [];
      let prepared = cache.get(key);
      if (!prepared) {
        prepared = {};
        for (const d of definitions) prepared[d.id] = prepare(pool, d.id);
        cache.set(key, prepared);
      }
      const result = {};
      for (const d of definitions) {
        result[d.id] = compare(c.metrics[d.id], without(prepared[d.id], c.id), Number(f.minN));
        if (f.market && ['impressions_per_day', 'impressions_to_views'].includes(d.id))
          result[d.id].reason = 'Website-market impressions unavailable';
      }
      return result;
    }
    const queue = universe
      .filter(
        (c) =>
          c.daysObserved > 0 &&
          (f.status === 'all' || c.status === f.status) &&
          (!f.search ||
            [c.name, c.offerBk, c.destination, c.country, c.owner, c.pcmName, c.strategicTerritory]
              .join(' ')
              .toLowerCase()
              .includes(f.search.toLowerCase())) &&
          (!f.ageMin || days(c.startDate, [c.endDate, cutoff].sort()[0]) >= Number(f.ageMin)) &&
          (!f.ageMax || days(c.startDate, [c.endDate, cutoff].sort()[0]) <= Number(f.ageMax)),
      )
      .map((c) => {
        const scores = comparisons(c, false),
          valid = Object.values(scores).filter((s) => finite(s.z));
        return {
          campaign: c,
          metricComparisons: scores,
          campaignAge: days(c.startDate, [c.endDate, cutoff].sort()[0]),
          below: valid.filter((s) => s.z < -0.5).length,
          scored: valid.length,
          minZ: valid.length ? Math.min(...valid.map((s) => s.z)) : null,
          weakestMetricId:
            Object.keys(scores)
              .filter((id) => finite(scores[id].z))
              .sort((a, b) => scores[a].z - scores[b].z)[0] || null,
        };
      })
      .sort(
        (a, b) =>
          b.below - a.below ||
          (a.minZ ?? Infinity) - (b.minZ ?? Infinity) ||
          a.campaign.name.localeCompare(b.campaign.name),
      );
    const requested = f.campaign ? queue.find((r) => r.campaign.id === f.campaign) : null,
      selected = (f.campaign ? requested : queue[0])?.campaign || null,
      metrics = selected ? comparisons(selected, true) : {},
      comparators = selected ? (groups.get(peerKey(selected, f)) || []).filter((c) => c.id !== selected.id) : [];
    let ageSensitivity = null;
    if (selected) {
      const age = days(selected.startDate, [selected.endDate, cutoff].sort()[0]),
        agePool = comparators.filter((c) => c.durationDays >= age);
      if (!f.observationFrom && !f.observationTo) {
        const ageSelected = measure(selected, { ...f, observationFrom: '', observationTo: '' }, cutoff, age),
          measured = agePool.map((c) => measure(c, { ...f, observationFrom: '', observationTo: '' }, cutoff, age)),
          scores = {};
        for (const d of definitions)
          scores[d.id] = compare(
            ageSelected.metrics[d.id],
            stats(measured.map((c) => c.metrics[d.id])),
            Number(f.minN),
          );
        ageSensitivity = {
          days: age,
          campaignCount: agePool.length,
          metrics: scores,
          reason:
            agePool.length < Number(f.minN) ? 'Not enough completed campaigns lasting at least ' + age + ' days.' : '',
        };
      } else
        ageSensitivity = {
          days: age,
          campaignCount: 0,
          metrics: {},
          reason: 'Clear the absolute observation date filters to compare the first ' + age + ' campaign days.',
        };
    }
    return {
      filters: f,
      errors: [],
      queue,
      selected,
      metrics,
      comparators,
      ageSensitivity,
      scope: {
        cutoff,
        universeCount: universe.length,
        queueCount: queue.length,
        cohort: f.cohort === 'primary' ? 'Completed campaigns lasting 28–42 days' : 'All completed campaigns',
        peerScope: f.peerScope,
        typeMatched: true,
        market: f.market || 'All source markets',
        unavailableMetrics: f.market ? ['impressions_per_day', 'impressions_to_views'] : [],
        minN: Number(f.minN),
        peerCount: comparators.length,
        selectionMissing: !!f.campaign && !selected,
        observation:
          f.observationFrom || f.observationTo
            ? 'Measure ' + (f.observationFrom || 'campaign launch') + ' to ' + (f.observationTo || cutoff)
            : 'Full observed campaign window',
        benchmarkFilters:
          'Peers always match campaign type. PCM, strategic territory, geography, owner, campaign type and launch dates apply to the queue and benchmark. Status, search and campaign age filter the queue only.',
      },
    };
  }
  function overview(snapshot, input) {
    const result = derive(snapshot, input),
      rows = result.queue,
      totalCampaigns = rows.length,
      metrics = {},
      totals = {},
      coverage = {},
      definitions = snapshot.metricDefinitions || [],
      weightedBy = {
        impressions_per_day: ['daysObserved', 'Observed campaign-days'],
        views_per_day: ['daysObserved', 'Observed campaign-days'],
        impressions_to_views: ['impressions', 'Current offer impressions'],
        packages_per_day: ['daysObserved', 'Observed campaign-days'],
        views_to_packages: ['views', 'Current offer views'],
        margin_per_day: ['daysObserved', 'Observed campaign-days'],
        margin_per_view: ['views', 'Current offer views'],
      };
    for (const key of ['impressions', 'views', 'packages', 'margin']) {
      const observed = rows.filter((row) => finite(row.campaign.totals[key]));
      totals[key] = observed.length ? observed.reduce((sum, row) => sum + row.campaign.totals[key], 0) : null;
      coverage[key] = { campaignsObserved: observed.length, totalCampaigns };
    }
    for (const definition of definitions) {
      const id = definition.id,
        [weightKey, weightBasis] = weightedBy[id] || ['', 'Unavailable'],
        unsupported = Boolean(result.filters.market && ['impressions_per_day', 'impressions_to_views'].includes(id)),
        counts = { below: 0, within: 0, above: 0, unavailable: 0 },
        matched = [];
      for (const row of rows) {
        const score = row.metricComparisons[id],
          weight = weightKey === 'daysObserved' ? row.campaign.daysObserved : row.campaign.totals[weightKey],
          eligible =
            !unsupported &&
            score &&
            finite(score.z) &&
            finite(score.value) &&
            finite(score.mean) &&
            finite(weight) &&
            weight > 0;
        if (eligible) {
          counts[score.classification]++;
          matched.push({
            id: row.campaign.id,
            actual: score.value,
            baseline: score.mean,
            weight,
          });
        } else counts.unavailable++;
      }
      const weightTotal = matched.reduce((sum, row) => sum + row.weight, 0),
        actual = weightTotal > 0 ? matched.reduce((sum, row) => sum + row.actual * row.weight, 0) / weightTotal : null,
        baseline =
          weightTotal > 0 ? matched.reduce((sum, row) => sum + row.baseline * row.weight, 0) / weightTotal : null,
        difference = finite(actual) && finite(baseline) ? actual - baseline : null,
        changePct = finite(actual) && finite(baseline) && baseline > 0 ? difference / baseline : null;
      metrics[id] = {
        actual,
        baseline,
        difference,
        changePct,
        matchedCampaigns: matched.length,
        matchedCampaignIds: matched.map((row) => row.id),
        totalCampaigns,
        ...counts,
        weightTotal,
        weightBasis,
        available: matched.length > 0,
        reason: unsupported
          ? 'Website-market impressions unavailable'
          : matched.length
            ? ''
            : 'No campaigns have a defensible individual comparison for this metric.',
        changePctReason:
          changePct !== null
            ? ''
            : baseline === null
              ? 'Comparable baseline unavailable'
              : 'Relative change requires a positive baseline',
      };
    }
    const { peerCount, selectionMissing, ...baseScope } = result.scope;
    return {
      filters: result.filters,
      errors: result.errors,
      metrics,
      totals,
      coverage,
      counts: {
        totalCampaigns,
        liveCampaigns: rows.filter((row) => row.campaign.status === 'live').length,
        completedCampaigns: rows.filter((row) => row.campaign.status === 'completed').length,
        withAnyScoredMetric: rows.filter((row) => row.scored > 0).length,
        withAnyBelowMetric: rows.filter((row) => row.below > 0).length,
        withoutScoredMetrics: rows.filter((row) => row.scored === 0).length,
      },
      scope: {
        ...baseScope,
        currentLabel: 'Matched current',
        baselineLabel: 'Comparable baseline',
        perDayLabel: 'Per campaign-day',
        aggregation:
          'Each metric compares the same eligible current campaigns against their own completed-peer means, weighted by current exposure. Matched campaigns can differ by metric.',
        weighting:
          'Per-day metrics use observed campaign-days; impressions-to-views uses current impressions; views-to-packages and margin-per-view use current views.',
        periodDescription:
          result.filters.observationFrom || result.filters.observationTo
            ? 'Selected observation dates, clipped to each campaign window and the common cutoff.'
            : 'Each campaign from launch through the common cutoff or its scheduled end; this is not a fixed rolling period.',
        campaignSelectionApplies: false,
        inference:
          'Descriptive matched-exposure comparison; no portfolio Z-score, standard deviation or significance claim.',
      },
    };
  }
  function overviewCsv(result, definitions) {
    if (result.errors.length) return '';
    const rows = [
      [
        'metric',
        'label',
        'unit',
        'current_matched',
        'baseline_matched',
        'difference',
        'relative_change',
        'compared_campaigns',
        'total_campaigns',
        'below',
        'within',
        'above',
        'unavailable',
        'weight_total',
        'weight_basis',
        'observation_period',
        'cutoff',
        'active_filters_json',
        'compared_campaign_ids',
        'reason',
      ],
      ...definitions.map((definition) => {
        const metric = result.metrics[definition.id];
        return [
          definition.id,
          definition.label,
          definition.unit,
          metric.actual,
          metric.baseline,
          metric.difference,
          metric.changePct,
          metric.matchedCampaigns,
          metric.totalCampaigns,
          metric.below,
          metric.within,
          metric.above,
          metric.unavailable,
          metric.weightTotal,
          metric.weightBasis,
          result.scope.observation,
          result.scope.cutoff,
          JSON.stringify(result.filters),
          JSON.stringify(metric.matchedCampaignIds),
          metric.reason,
        ];
      }),
    ];
    return rows.map((row) => row.map((value) => csvCell(value)).join(',')).join('\r\n');
  }
  function csv(result, definitions) {
    if (!result.selected) return '';
    const c = result.selected,
      rows = [
        [
          'campaign_id',
          'campaign',
          'country',
          'destination',
          'observed_start',
          'observed_end',
          'website_market',
          'campaign_type',
          'owner',
          'pcm_name',
          'strategic_territory',
          'active_filters_json',
          'peer_scope',
          'cohort',
          'min_n',
          'metric',
          'value',
          'mean',
          'sd',
          'n',
          'z',
          'classification',
          'reason',
        ],
        ...definitions.map((d) => {
          const s = result.metrics[d.id];
          return [
            c.id,
            c.name,
            c.country,
            c.destination,
            c.observedStart,
            c.observationEnd,
            result.filters.market || 'All source markets',
            c.campaignType,
            c.owner,
            c.pcmName || 'No current-quarter PCM record',
            c.strategicTerritory || 'Unknown strategic territory',
            JSON.stringify(result.filters),
            result.filters.peerScope,
            result.filters.cohort,
            result.filters.minN,
            d.id,
            s.value,
            s.mean,
            s.sd,
            s.n,
            s.z,
            s.classification,
            s.reason,
          ];
        }),
      ];
    return rows.map((r) => r.map((v) => csvCell(v)).join(',')).join('\r\n');
  }
  const OP_MIN_PEERS = 5,
    OP_MIN_DAYS = 7,
    OP_MIN_VIEWS = 500;
  const opUnknown = (value) => !value || /^unknown|^unassigned/i.test(value);
  const opMoney = (value) => 'A$' + Math.round(Math.abs(value)).toLocaleString('en-AU');
  const opCompactMoney = (value) =>
    Math.abs(value) >= 1000000
      ? 'A$' + (Math.abs(value) / 1000000).toFixed(1) + 'M'
      : Math.abs(value) >= 1000
        ? 'A$' + Math.round(Math.abs(value) / 1000) + 'k'
        : opMoney(value);
  function opFilters(input) {
    return {
      ...filters(input || {}),
      cohort: 'primary',
      peerScope: 'destination',
      minN: String(OP_MIN_PEERS),
    };
  }
  function reviewValue(campaign, reviewBy) {
    return reviewBy === 'pcm'
      ? campaign.pcmName || 'No current-quarter PCM record'
      : campaign.strategicTerritory || 'Unknown strategic territory';
  }
  function inReview(campaign, f) {
    return !f.reviewBy || reviewValue(campaign, f.reviewBy) === f.reviewGroup;
  }
  function ownerSalesforce(campaign) {
    return campaign.pcmName === 'No current-quarter PCM record' &&
      typeof campaign.ownerSalesforce === 'string' &&
      campaign.ownerSalesforce.trim()
      ? campaign.ownerSalesforce.trim()
      : null;
  }
  function zeroSale(campaign) {
    return (
      campaign.status === 'live' &&
      campaign.coverage.sales === 0 &&
      campaign.coverage.views >= OP_MIN_DAYS &&
      finite(campaign.totals.views) &&
      campaign.totals.views >= OP_MIN_VIEWS
    );
  }
  function peerGroup(campaign, peers, minimum = OP_MIN_PEERS) {
    const sameTypeCountry = peers.filter(
        (peer) =>
          peer.id !== campaign.id &&
          !opUnknown(campaign.country) &&
          peer.country === campaign.country &&
          peer.campaignType === campaign.campaignType,
      ),
      destination = opUnknown(campaign.destination)
        ? []
        : sameTypeCountry.filter((peer) => peer.destination === campaign.destination),
      salesN = (pool) => pool.filter((peer) => peer.coverage.sales > 0 && finite(peer.metrics.margin_per_day)).length,
      destinationN = salesN(destination),
      countryN = salesN(sameTypeCountry),
      level =
        destinationN >= minimum ? 'destination' : destinationN < minimum && countryN >= minimum ? 'country' : null,
      pool = level === 'destination' ? destination : level === 'country' ? sameTypeCountry : [];
    return {
      level,
      key: level
        ? campaign.country + '|' + (level === 'destination' ? campaign.destination + '|' : '') + campaign.campaignType
        : null,
      n: level === 'destination' ? destinationN : level === 'country' ? countryN : 0,
      destinationN,
      countryN,
      peers: pool,
    };
  }
  function uncomparableReason(row) {
    if (row.comparable) return null;
    if (opUnknown(row.destination)) return 'unknown_destination';
    if (opUnknown(row.country)) return 'unknown_country';
    if (row.destinationPeerSalesN < OP_MIN_PEERS && row.countryPeerSalesN < OP_MIN_PEERS) return 'too_few_peers';
    if (row.coverage.sales === 0) return 'no_sales_rows';
    if (row.coverage.impressions === 0) return 'no_impression_rows';
    return 'no_peer_variation';
  }
  function opSearch(campaign, search) {
    return (
      !search ||
      [
        campaign.name,
        campaign.opportunityName,
        campaign.offerBk,
        campaign.country,
        campaign.destination,
        campaign.owner,
        campaign.pcmName,
        campaign.strategicTerritory,
      ]
        .join(' ')
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }
  function opRow(campaign, peers, definitions, cutoff) {
    const group = peerGroup(campaign, peers),
      inferredZero = zeroSale(campaign),
      actual = { ...campaign.metrics },
      totals = { ...campaign.totals },
      tooEarly = campaign.daysObserved < OP_MIN_DAYS,
      metrics = {},
      peerMedian = {},
      q1 = {},
      q3 = {},
      positions = {};
    for (const definition of definitions) {
      const id = definition.id,
        comparison = compare(actual[id], stats(group.peers.map((peer) => peer.metrics[id])), OP_MIN_PEERS);
      comparison.rawPositionQuartile = comparison.positionQuartile;
      if (tooEarly) comparison.positionQuartile = 'unscored';
      metrics[id] = comparison;
      peerMedian[id] = comparison.median;
      q1[id] = comparison.q1;
      q3[id] = comparison.q3;
      positions[id] = comparison.positionQuartile;
    }
    const margin = metrics.margin_per_day,
      comparable = finite(margin?.z),
      steps = ['impressions_to_views', 'views_to_packages', 'margin_per_view'],
      weakestStep = steps.reduce(
        (weakest, id) => (finite(metrics[id]?.z) && (!weakest || metrics[id].z <= metrics[weakest].z) ? id : weakest),
        null,
      ),
      daysLeft = Math.max(0, days(cutoff, campaign.endDate) - 1),
      gap = comparable ? (daysLeft > 0 ? (actual.margin_per_day - margin.median) * Math.min(daysLeft, 28) : 0) : null;
    const row = {
      id: campaign.id,
      offerBk: campaign.offerBk,
      name: campaign.opportunityName || campaign.name,
      vendorName: campaign.name,
      destination: campaign.destination,
      country: campaign.country,
      territory: campaign.strategicTerritory || 'Unknown strategic territory',
      pcm: campaign.pcmName || 'No current-quarter PCM record',
      ownerSalesforce: ownerSalesforce(campaign),
      type: campaign.campaignType,
      startDate: campaign.startDate,
      endDate: campaign.endDate,
      durationDays: campaign.durationDays,
      ageDays: days(campaign.startDate, [campaign.endDate, cutoff].sort()[0]),
      daysObserved: campaign.daysObserved,
      daysLeft,
      totals,
      coverage: campaign.coverage,
      campaign,
      actual,
      peerMedian,
      q1,
      q3,
      metrics,
      positions,
      n: margin?.n || 0,
      level: group.level,
      peerKey: group.key,
      destinationPeerSalesN: group.destinationN,
      countryPeerSalesN: group.countryN,
      comparable,
      tooEarly,
      zeroSale: inferredZero,
      weakestStep,
      gap,
      marginToDate: totals.margin,
      actualMarginPerDay: actual.margin_per_day,
      peerMedianMarginPerDay: margin?.median ?? null,
      reason: null,
    };
    row.reason = uncomparableReason(row);
    return { row, group };
  }
  function rollup(rows, key) {
    const groups = new Map(),
      field = key === 'pcmName' ? 'pcm' : 'territory';
    for (const row of rows.filter((item) => item.campaign.status === 'live')) {
      const value = row[field];
      if (!groups.has(value)) groups.set(value, []);
      groups.get(value).push(row);
    }
    return [...groups.entries()]
      .map(([key, members]) => {
        const comparable = members.filter((row) => row.comparable),
          mature = comparable.filter((row) => !row.tooEarly);
        return {
          key,
          nLive: members.length,
          nComparable: comparable.length,
          nOnCountryPeers: comparable.filter((row) => row.level === 'country').length,
          below: mature.filter((row) => row.positions.margin_per_day === 'below').length,
          inLine: mature.filter((row) => row.positions.margin_per_day === 'in_line').length,
          above: mature.filter((row) => row.positions.margin_per_day === 'above').length,
          tooEarly: comparable.length - mature.length,
          sumBehind: mature
            .filter((row) => row.positions.margin_per_day === 'below' && finite(row.gap) && row.gap < 0)
            .reduce((sum, row) => sum - row.gap, 0),
          sumMarginToDateAllLive: members.reduce((sum, row) => sum + (row.marginToDate || 0), 0),
          sumMarginToDateComparable: comparable.reduce((sum, row) => sum + (row.marginToDate || 0), 0),
        };
      })
      .sort((a, b) => b.sumMarginToDateAllLive - a.sumMarginToDateAllLive || a.key.localeCompare(b.key));
  }
  function trajectory(campaign, peers, measureName = 'packages', options = {}) {
    const cutoff = options.cutoff || campaign.observationEnd || campaign.endDate,
      market = options.market || '',
      first = [campaign.startDate, options.observationFrom || campaign.startDate].sort().at(-1),
      last = [campaign.endDate, cutoff, options.observationTo || cutoff].sort()[0],
      lastDay = Math.max(0, days(campaign.startDate, last)),
      records = (item) =>
        (market ? item.dailyByMarket?.[market] || [] : item.daily || []).filter(
          (row) =>
            row.date >= item.startDate &&
            row.date <= item.endDate &&
            row.date <= cutoff &&
            (!options.observationFrom || row.date >= options.observationFrom) &&
            (!options.observationTo || row.date <= options.observationTo),
        ),
      series = (item) => {
        const rows = records(item),
          byDate = new Map(rows.map((row) => [row.date, row]));
        const observed = rows.filter((row) => row.salesObserved && finite(row[measureName]));
        return {
          byDate,
          firstSale: observed.length ? observed[0].date : null,
          reach: rows.length ? rows.at(-1).date : null,
          hasSales: observed.length > 0,
        };
      },
      own = series(campaign),
      candidates = peers
        .filter((peer) => peer.id !== campaign.id)
        .map((peer) => ({ peer, ...series(peer), cumulative: 0 }))
        .filter((peer) => peer.hasSales),
      minimumRemaining = Math.max(OP_MIN_PEERS, Math.ceil(candidates.length / 2)),
      points = [];
    let cumulative = 0;
    for (let day = 1; day <= lastDay; day++) {
      const date = add(campaign.startDate, day - 1),
        record = own.byDate.get(date),
        beforeObservation = date < first;
      if (record?.salesObserved && finite(record[measureName])) cumulative += record[measureName];
      const values = [];
      for (const peer of candidates) {
        const peerDate = add(peer.peer.startDate, day - 1),
          peerRecord = peer.byDate.get(peerDate);
        if (peerRecord?.salesObserved && finite(peerRecord[measureName])) peer.cumulative += peerRecord[measureName];
        if (
          !beforeObservation &&
          peer.firstSale &&
          peerDate >= peer.firstSale &&
          peer.reach &&
          peerDate <= peer.reach &&
          peerDate <= peer.peer.endDate
        )
          values.push(peer.cumulative);
      }
      const distribution = stats(values),
        bandAvailable = values.length >= minimumRemaining,
        actual =
          !beforeObservation && own.reach && date <= own.reach && own.firstSale && date >= own.firstSale
            ? cumulative
            : null;
      points.push({
        day,
        date,
        actual,
        q1: bandAvailable ? distribution.q1 : null,
        median: bandAvailable ? distribution.median : null,
        q3: bandAvailable ? distribution.q3 : null,
        peersRemaining: values.length,
        bandAvailable,
        observedOnDay: Boolean(record),
        salesObservedOnDay: Boolean(record?.salesObserved),
        inferredZero: false,
      });
    }
    const banded = points.filter((point) => point.bandAvailable);
    return {
      measure: measureName,
      market,
      points,
      peerCount: candidates.length,
      minimumRemaining,
      bandStartDay: banded[0]?.day ?? null,
      bandEndDay: banded.at(-1)?.day ?? null,
      lastBandedPoint: banded.at(-1) || null,
      note: 'Recorded cumulative values. Missing daily sales records do not confirm zero activity. Peer prefixes before their first sales record are unavailable; recorded cumulative values carry through interior gaps and stop at the last available fact date. The peer mix changes with age.',
    };
  }
  function weekly(snapshot, input = {}) {
    const f = opFilters(input),
      cutoff = [
        snapshot.meta.observationCutoff || snapshot.meta.asOf,
        f.observationTo || snapshot.meta.observationCutoff || snapshot.meta.asOf,
      ].sort()[0],
      buckets = new Map(),
      excludedIds = exclusionIds(f.weeklyExclusion),
      blank = () => ({
        margin: 0,
        packages: 0,
        views: 0,
        impressions: f.market ? null : 0,
        recordedCampaignDays: 0,
        liveCampaignDays: 0,
        marginPerCampaignDay: null,
      }),
      monday = (date) => add(date, -((new Date(date + 'T00:00:00Z').getUTCDay() + 6) % 7));
    let earliest = null;
    for (const campaign of snapshot.campaigns.filter((item) => dimensions(item, f) && opSearch(item, f.search))) {
      const facts = f.market ? campaign.dailyByMarket?.[f.market] || [] : campaign.daily || [];
      for (const row of facts) {
        if (
          row.date < campaign.startDate ||
          row.date > campaign.endDate ||
          row.date > cutoff ||
          (f.observationFrom && row.date < f.observationFrom)
        )
          continue;
        if (![row.impressionObserved, row.viewsObserved, row.salesObserved].some((flag) => flag === true)) continue;
        const key = monday(row.date);
        if (!earliest || key < earliest) earliest = key;
        if (!buckets.has(key)) buckets.set(key, { allIn: blank(), filtered: blank() });
        for (const field of ['allIn', 'filtered']) {
          if (field === 'filtered' && excludedIds.includes(campaign.id)) continue;
          const entry = buckets.get(key)[field];
          for (const measureName of ['margin', 'packages', 'views', 'impressions'])
            if (finite(row[measureName])) entry[measureName] += row[measureName];
          entry.recordedCampaignDays++;
        }
      }
    }
    const weeks = [];
    if (earliest)
      for (let start = earliest; start <= cutoff; start = add(start, 7)) {
        const calendarWeekEnd = add(start, 6),
          weekEnd = [calendarWeekEnd, cutoff].sort()[0],
          bucket = buckets.get(start) || {
            allIn: blank(),
            filtered: blank(),
          };
        for (const field of ['allIn', 'filtered']) {
          const value = bucket[field];
          value.liveCampaignDays = value.recordedCampaignDays;
          value.marginPerCampaignDay = value.recordedCampaignDays ? value.margin / value.recordedCampaignDays : null;
        }
        weeks.push({
          weekStart: start,
          weekEnd,
          calendarWeekEnd,
          daysInWeek: days([start, f.observationFrom || start].sort().at(-1), weekEnd),
          partial: calendarWeekEnd > cutoff || Boolean(f.observationFrom && start < f.observationFrom),
          ...bucket,
        });
      }
    const fullWeeks = weeks.filter((week) => !week.partial).slice(-12),
      displayStart = fullWeeks.length === 12 ? fullWeeks[0].weekStart : weeks[0]?.weekStart;
    return {
      weeks: [...fullWeeks, ...weeks.filter((week) => week.partial && week.weekStart >= displayStart)].sort((a, b) =>
        a.weekStart.localeCompare(b.weekStart),
      ),
      weeklyExclusion: f.weeklyExclusion,
      exclusionIds: excludedIds,
      excludedCampaigns: snapshot.campaigns
        .filter((c) => excludedIds.includes(c.id))
        .map((c) => ({ id: c.id, name: c.opportunityName || c.name, startDate: c.startDate })),
      invalidExclusionIds: excludedIds.filter((id) => !snapshot.campaigns.some((c) => c.id === id)),
      population:
        'All Flash campaign windows with recorded facts in each ISO week, live and completed. Recorded campaign-days count fact-bearing campaign/date rows, not scheduled deals.',
      scope:
        'Active geography, PCM, territory, owner, type, launch, search, website-market and observation filters apply; queue status and age do not restrict weekly history.',
      cutoff,
    };
  }
  function weeklyStory(input) {
    const baseline = !Array.isArray(input) && input.exclusionIds?.length > 0 ? 'filtered' : 'allIn',
      weeks = (Array.isArray(input) ? input : input.weeks).filter((week) => !week.partial).slice(-12),
      emptyHero = {
        direction: 'unavailable',
        amount: null,
        signedAmount: null,
        changePct: null,
        currentWeeklyMargin: null,
        priorWeeklyMargin: null,
        priorStart: null,
        priorEnd: null,
        recentStart: null,
        recentEnd: null,
        recordedDaysShare: null,
        rateShare: null,
      };
    const selectedWeeks = weeks.map((week) => week[baseline]);
    const invalidExclusions = !Array.isArray(input) && input.invalidExclusionIds?.length;
    const tooManyExclusions = !Array.isArray(input) && input.exclusionIds?.length > MAX_WEEKLY_EXCLUSIONS;
    const emptyPeriod =
      weeks.length === 12 &&
      (selectedWeeks.slice(0, 7).every((week) => !week.recordedCampaignDays) ||
        selectedWeeks.slice(7).every((week) => !week.recordedCampaignDays));
    if (weeks.length < 12 || invalidExclusions || tooManyExclusions || emptyPeriod)
      return {
        available: false,
        baseline,
        reason: tooManyExclusions
          ? 'Choose up to 50 campaign exclusions.'
          : invalidExclusions
            ? 'Some excluded campaigns are unavailable in this snapshot. Review exclusions.'
            : emptyPeriod
              ? 'No recorded campaign activity remains in one or both comparison periods.'
              : 'Twelve full weeks of this scope are required for the weekly comparison.',
        allIn: null,
        filtered: null,
        hero: emptyHero,
      };
    const period = (items, field) => {
        const margin = items.reduce((sum, week) => sum + week[field].margin, 0),
          recorded = items.reduce((sum, week) => sum + week[field].recordedCampaignDays, 0);
        return {
          start: items[0].weekStart,
          end: items.at(-1).calendarWeekEnd || items.at(-1).weekEnd,
          weeks: items.length,
          avgWeeklyMargin: margin / items.length,
          avgWeeklyRecordedCampaignDays: recorded / items.length,
          marginPerCampaignDay: recorded ? margin / recorded : null,
        };
      },
      changes = (field) => {
        const prior = period(weeks.slice(0, 7), field),
          recent = period(weeks.slice(7), field),
          ratio = (a, b) => (finite(a) && finite(b) && b > 0 ? a / b - 1 : null),
          log = (a, b) => (finite(a) && finite(b) && a > 0 && b > 0 ? Math.log(a / b) : null),
          totalLog = log(recent.avgWeeklyMargin, prior.avgWeeklyMargin),
          recordedDaysLog = log(recent.avgWeeklyRecordedCampaignDays, prior.avgWeeklyRecordedCampaignDays),
          rateLog = log(recent.marginPerCampaignDay, prior.marginPerCampaignDay),
          validSplit = finite(totalLog) && Math.abs(totalLog) > 1e-12 && finite(recordedDaysLog) && finite(rateLog);
        return {
          prior,
          recent,
          marginChangePct: ratio(recent.avgWeeklyMargin, prior.avgWeeklyMargin),
          marginChangePerWeek: recent.avgWeeklyMargin - prior.avgWeeklyMargin,
          recordedDaysChangePct: ratio(recent.avgWeeklyRecordedCampaignDays, prior.avgWeeklyRecordedCampaignDays),
          rateChangePct: ratio(recent.marginPerCampaignDay, prior.marginPerCampaignDay),
          logSplit: {
            totalLog,
            recordedDaysLog,
            rateLog,
            recordedDaysShare: validSplit ? recordedDaysLog / totalLog : null,
            rateShare: validSplit ? rateLog / totalLog : null,
          },
        };
      },
      allIn = changes('allIn'),
      filtered = changes('filtered'),
      selected = baseline === 'filtered' ? filtered : allIn,
      value = selected.marginChangePerWeek;
    return {
      available: true,
      baseline,
      reason: '',
      allIn,
      filtered,
      hero: {
        direction: value < 0 ? 'down' : value > 0 ? 'up' : 'flat',
        amount: Math.abs(value),
        signedAmount: value,
        changePct: selected.marginChangePct,
        currentWeeklyMargin: selected.recent.avgWeeklyMargin,
        priorWeeklyMargin: selected.prior.avgWeeklyMargin,
        priorStart: selected.prior.start,
        priorEnd: selected.prior.end,
        recentStart: selected.recent.start,
        recentEnd: selected.recent.end,
        recordedDaysShare: selected.logSplit.recordedDaysShare,
        rateShare: selected.logSplit.rateShare,
      },
    };
  }
  function verdict(result) {
    const portfolio = [],
      campaign = [],
      story = result.story,
      counts = result.counts,
      coverage = result.coverage,
      headline = result.headline,
      population =
        result.filters.status === 'live' ? 'live' : result.filters.status === 'completed' ? 'completed' : 'selected';
    if (story.available && story[story.baseline || 'allIn']) {
      const value = story[story.baseline || 'allIn'],
        change = value.marginChangePct;
      portfolio.push(
        'Recorded Flash margin' +
          (story.baseline === 'filtered' ? ' with campaign exclusions' : '') +
          ' is ' +
          opCompactMoney(value.recent.avgWeeklyMargin) +
          ' a week, ' +
          (value.marginChangePerWeek < 0 ? 'down ' : value.marginChangePerWeek > 0 ? 'up ' : 'unchanged ') +
          (finite(change) ? Math.abs(change * 100).toFixed(1) + '% ' : '') +
          'in ' +
          value.recent.start +
          ' to ' +
          value.recent.end +
          ' versus ' +
          value.prior.start +
          ' to ' +
          value.prior.end +
          '.',
      );
      if (finite(value.logSplit.recordedDaysShare)) {
        const share = value.logSplit.recordedDaysShare;
        portfolio.push(
          'The log split associates ' +
            (share * 100).toFixed(1) +
            '% of that change with recorded campaign-days and ' +
            (value.logSplit.rateShare * 100).toFixed(1) +
            '% with margin per recorded campaign-day; this does not establish cause.',
        );
      }
    } else portfolio.push(story.reason);
    portfolio.push(
      'Compared campaigns with at least seven observed days: ' +
        counts.below +
        ' well below peers, ' +
        counts.inLine +
        ' in line and ' +
        counts.above +
        ' well above; ' +
        counts.tooEarly +
        ' more are too early.',
    );
    if (headline.count)
      portfolio.push(
        'The ' +
          headline.count +
          ' well-below campaigns with seven or more days are about ' +
          opCompactMoney(headline.amount) +
          " behind peer median pace over their remaining days, capped at 28, at today's rates. " +
          headline.topDestinations
            .slice(0, 2)
            .map((row) => row.name)
            .join(' and ') +
          ' carry ' +
          (100 * (headline.top2Share || 0)).toFixed(0) +
          '%. This is a rate gap before refunds, not a forecast.',
      );
    else
      portfolio.push(
        'No well-below campaigns with seven or more observed days contribute to the forward rate-gap figure in this scope.',
      );
    portfolio.push(
      coverage.compared +
        ' of ' +
        coverage.total +
        ' ' +
        population +
        ' campaigns' +
        (finite(coverage.marginShare) ? ' (' + (coverage.marginShare * 100).toFixed(1) + '% of recorded margin)' : '') +
        ' have a peer group, ' +
        coverage.onCountryPeers +
        ' on explicitly labelled country peers.',
    );
    const selected = result.selected;
    if (selected) {
      const prefix = result.filters.market ? 'Seen from the ' + result.filters.market + ' website market, ' : '';
      if (selected.tooEarly)
        campaign.push(
          prefix +
            'day ' +
            selected.daysObserved +
            ' of ' +
            selected.durationDays +
            ' is too early for a position band.',
        );
      else if (!selected.comparable)
        campaign.push(
          prefix +
            'no peer group can support a margin comparison under this scope (' +
            selected.reason.replace(/_/g, ' ') +
            ').',
        );
      else {
        const label =
          selected.positions.margin_per_day === 'below'
            ? 'well below'
            : selected.positions.margin_per_day === 'above'
              ? 'well above'
              : 'in line with';
        campaign.push(
          prefix +
            selected.name +
            ' is ' +
            label +
            ' its ' +
            selected.n +
            ' ' +
            (selected.level === 'country' ? selected.country + ' country' : selected.destination) +
            ' peers on margin per day.',
        );
        campaign.push(
          'Margin per day is ' +
            (selected.actualMarginPerDay < 0 ? '-' : '') +
            opMoney(selected.actualMarginPerDay) +
            ' against a peer median of ' +
            (selected.peerMedianMarginPerDay < 0 ? '-' : '') +
            opMoney(selected.peerMedianMarginPerDay) +
            (selected.weakestStep
              ? '. Weakest recorded step: ' +
                {
                  impressions_to_views: 'offer impressions to views',
                  views_to_packages: 'offer views to packages',
                  margin_per_view: 'margin per offer view',
                }[selected.weakestStep]
              : '') +
            '.',
        );
        campaign.push(
          (selected.gap < 0 ? '-' : '+') +
            opMoney(selected.gap) +
            " versus peer median pace at today's rate over " +
            Math.min(selected.daysLeft, 28) +
            ' remaining days. This is not a forecast.',
        );
      }
      if (selected.zeroSale)
        campaign.push(
          'No sales rows in ' +
            selected.daysObserved +
            ' observed days and ' +
            Math.round(selected.totals.views).toLocaleString('en-AU') +
            ' recorded views; packages and margin remain unknown until sales coverage is verified.',
        );
      if (selected.level === 'country')
        campaign.push(
          'Country peers are used because the destination group has fewer than five peers with sales records.',
        );
    }
    return { portfolio, campaign };
  }
  const PREVIOUS_METRICS = {
    impressions_per_day: ['impressions', 'days'],
    views_per_day: ['views', 'days'],
    impressions_to_views: ['views', 'impressions'],
    packages_per_day: ['packages', 'days'],
    views_to_packages: ['packages', 'views'],
    margin_per_day: ['margin', 'days'],
    margin_per_view: ['margin', 'views'],
  };
  function previousRuns(snapshot, input = {}, currentRows, selected) {
    const f = opFilters(input),
      cutoff = snapshot.meta.observationCutoff || snapshot.meta.asOf,
      observationIncompatible = Boolean(f.observationFrom || f.observationTo),
      rawFilters = { ...f, observationFrom: '', observationTo: '' },
      scope = {
        asOf: snapshot.meta.asOf,
        cutoff,
        historyStart:
          snapshot.sourceCapabilities?.propertyIdentity?.historyStartDate ||
          snapshot.campaigns.reduce(
            (date, campaign) => (campaign.startDate < date ? campaign.startDate : date),
            cutoff,
          ),
        market: f.market || 'All website markets',
        minimumSummaryDays: OP_MIN_DAYS,
        selectionRule:
          'Current portfolio filters and group review restrict current campaigns only. Previous runs come from all snapshot history, irrespective of prior owner, PCM, territory, destination, status or launch-date filters. Choose the most recent earlier launch of the same stable identity and exact campaign type; never skip an unavailable, tied or overlapping immediate previous run.',
        identityRule:
          'Use a unique stable property ID. With missing property identity only, use the exact offer ID and label that narrower basis. Ambiguous identity is unavailable. Names, vendors and accounts do not establish identity. Property mapping is current metadata, not a reconstruction of assignment at each launch. History is limited to campaign windows retained in this snapshot.',
        observationRule:
          'Compare the first N calendar days after each launch, N = minimum of current launch age and both campaign durations. Absolute observation-date filters are incompatible and withhold this comparison. Website market applies to both runs; market impressions are unavailable. No zero-sale inference is made. Summary pools only metric-valid pairs with N >= 7; each metric can have a different paired set. This is descriptive, not a causal comparison.',
      },
      window = (campaign, n) => {
        const measured = measure(campaign, rawFilters, cutoff, n);
        return {
          id: campaign.id,
          startDate: campaign.startDate,
          endDate: campaign.endDate,
          windowStart: measured.observedStart,
          windowEnd: measured.observationEnd,
          totals: measured.totals,
          coverage: measured.coverage,
        };
      },
      identity = (campaign) => {
        if (campaign.propertyIdentityStatus === 'ambiguous')
          return { basis: null, key: null, reason: 'identity_ambiguous' };
        if (campaign.propertyIdentityStatus === 'unique')
          return campaign.propertyId
            ? { basis: 'property', key: campaign.propertyId, reason: null }
            : { basis: null, key: null, reason: 'missing_stable_identity' };
        return campaign.offerBk
          ? { basis: 'offer', key: campaign.offerBk, reason: null }
          : { basis: null, key: null, reason: 'missing_stable_identity' };
      },
      index = new Map();
    for (const campaign of snapshot.campaigns) {
      const keys = new Set();
      if (campaign.offerBk) keys.add(JSON.stringify(['offer', campaign.offerBk, campaign.campaignType]));
      if (campaign.propertyIdentityStatus === 'unique' && campaign.propertyId)
        keys.add(JSON.stringify(['property', campaign.propertyId, campaign.campaignType]));
      if (campaign.propertyIdentityStatus === 'ambiguous')
        for (const id of campaign.propertyIds || []) keys.add(JSON.stringify(['property', id, campaign.campaignType]));
      for (const key of keys) {
        if (!index.has(key)) index.set(key, []);
        index.get(key).push(campaign);
      }
    }
    for (const candidates of index.values()) candidates.sort((a, b) => b.startDate.localeCompare(a.startDate));
    const pair = (campaign) => {
      const match = identity(campaign),
        ageDays = Math.max(0, days(campaign.startDate, [campaign.endDate, cutoff].sort()[0])),
        candidates = (index.get(JSON.stringify([match.basis, match.key, campaign.campaignType])) || []).filter(
          (candidate) => candidate.id !== campaign.id && candidate.startDate < campaign.startDate,
        ),
        latest = candidates[0],
        tied = latest && candidates.filter((candidate) => candidate.startDate === latest.startDate).length > 1;
      let reason = match.reason,
        prior = tied ? null : latest || null;
      if (!reason && !latest) reason = 'no_previous_run_in_history';
      if (!reason && tied) reason = 'ambiguous_previous_run';
      if (!reason && prior?.propertyIdentityStatus === 'ambiguous') reason = 'previous_identity_ambiguous';
      if (!reason && prior.endDate >= campaign.startDate) reason = 'overlapping_previous_run';
      if (observationIncompatible) reason = 'absolute_observation_window_incompatible';
      const matchedDays = prior ? Math.min(ageDays, campaign.durationDays, prior.durationDays) : 0;
      if (!reason && matchedDays < 1) reason = 'no_shared_launch_age';
      const current = window(campaign, matchedDays || Math.max(1, ageDays)),
        previous = prior ? window(prior, matchedDays || 1) : null,
        metrics = {};
      for (const [id, [numerator, denominator]] of Object.entries(PREVIOUS_METRICS)) {
        const sideValue = (side) => {
            if (!side) return null;
            const a = side.totals[numerator],
              b = denominator === 'days' ? matchedDays : side.totals[denominator];
            return finite(a) && finite(b) && b > 0 ? a / b : null;
          },
          a = sideValue(current),
          b = sideValue(previous);
        let metricReason = reason;
        if (!metricReason && f.market && ['impressions_per_day', 'impressions_to_views'].includes(id))
          metricReason = 'market_impressions_unavailable';
        if (!metricReason && (!finite(a) || !finite(b))) {
          const missing = (side) =>
              !side || !finite(side.totals[numerator]) || (denominator !== 'days' && !finite(side.totals[denominator])),
            missingCurrent = missing(current),
            missingPrior = missing(previous);
          metricReason =
            missingCurrent && missingPrior
              ? 'missing_source_both'
              : missingCurrent
                ? 'missing_source_current'
                : missingPrior
                  ? 'missing_source_previous'
                  : !finite(a) && !finite(b)
                    ? 'zero_denominator_both'
                    : !finite(a)
                      ? 'zero_denominator_current'
                      : 'zero_denominator_previous';
        }
        metrics[id] = {
          current: reason ? null : a,
          prior: reason ? null : b,
          difference: !metricReason ? a - b : null,
          changePct: !metricReason && b > 0 ? (a - b) / b : null,
          available: !metricReason,
          reason: metricReason,
        };
      }
      const available = !reason && Object.values(metrics).some((metric) => metric.available);
      return {
        currentId: campaign.id,
        name: campaign.opportunityName || campaign.name,
        priorId: prior?.id || null,
        priorName: prior ? prior.opportunityName || prior.name : null,
        matchBasis: match.basis,
        identityKey: match.key,
        available,
        reason: reason || (available ? null : 'no_source_valid_metrics'),
        ageDays,
        matchedDays,
        currentDurationDays: campaign.durationDays,
        priorDurationDays: prior?.durationDays || null,
        truncated: Boolean(prior && matchedDays < Math.min(ageDays, campaign.durationDays)),
        current,
        prior: previous,
        metrics,
      };
    };
    if (!currentRows) {
      const invalid =
        errors(f, cutoff).length ||
        (f.market &&
          (!snapshot.sourceCapabilities?.market?.supported ||
            !snapshot.sourceCapabilities.market.values?.includes(f.market)));
      const campaigns = invalid
        ? []
        : snapshot.campaigns
            .filter((campaign) => dimensions(campaign, f))
            .map((campaign) => measure(campaign, f, cutoff));
      currentRows = campaigns
        .filter(
          (campaign) =>
            campaign.daysObserved > 0 &&
            (f.status === 'all' || campaign.status === f.status) &&
            opSearch(campaign, f.search) &&
            inReview(campaign, f) &&
            (!f.ageMin || days(campaign.startDate, [campaign.endDate, cutoff].sort()[0]) >= Number(f.ageMin)) &&
            (!f.ageMax || days(campaign.startDate, [campaign.endDate, cutoff].sort()[0]) <= Number(f.ageMax)),
        )
        .map((campaign) => ({ campaign }));
      const picked = campaigns.find(
        (campaign) => campaign.id === f.campaign && campaign.daysObserved > 0 && inReview(campaign, f),
      );
      selected = picked ? { campaign: picked } : null;
    }
    const rows = currentRows.map((row) => pair(row.campaign)),
      selectedRow = selected
        ? rows.find((row) => row.currentId === selected.campaign.id) || pair(selected.campaign)
        : null,
      reasons = {},
      metrics = {};
    for (const row of rows) {
      const reason = row.reason || (row.matchedDays < OP_MIN_DAYS ? 'too_early_for_summary' : null);
      if (reason) reasons[reason] = (reasons[reason] || 0) + 1;
    }
    for (const [id, [numerator, denominator]] of Object.entries(PREVIOUS_METRICS)) {
      const valid = rows.filter((row) => row.matchedDays >= OP_MIN_DAYS && row.metrics[id].available),
        currentNumerator = valid.reduce((sum, row) => sum + row.current.totals[numerator], 0),
        priorNumerator = valid.reduce((sum, row) => sum + row.prior.totals[numerator], 0),
        pairedDays = valid.reduce((sum, row) => sum + row.matchedDays, 0),
        currentDenominator =
          denominator === 'days' ? pairedDays : valid.reduce((sum, row) => sum + row.current.totals[denominator], 0),
        priorDenominator =
          denominator === 'days' ? pairedDays : valid.reduce((sum, row) => sum + row.prior.totals[denominator], 0),
        a = currentDenominator > 0 ? currentNumerator / currentDenominator : null,
        b = priorDenominator > 0 ? priorNumerator / priorDenominator : null;
      metrics[id] = {
        current: a,
        prior: b,
        difference: finite(a) && finite(b) ? a - b : null,
        changePct: finite(a) && finite(b) && b > 0 ? (a - b) / b : null,
        pairCount: valid.length,
        pairedDays,
        reasons: Object.fromEntries(
          [
            ...new Set(
              rows
                .map(
                  (row) => row.metrics[id].reason || (row.matchedDays < OP_MIN_DAYS ? 'too_early_for_summary' : null),
                )
                .filter(Boolean),
            ),
          ].map((reason) => [
            reason,
            rows.filter(
              (row) =>
                (row.metrics[id].reason || (row.matchedDays < OP_MIN_DAYS ? 'too_early_for_summary' : null)) === reason,
            ).length,
          ]),
        ),
        currentNumerator,
        priorNumerator,
        currentDenominator,
        priorDenominator,
      };
    }
    return {
      summary: {
        totalCampaigns: rows.length,
        matchedCampaigns: rows.filter((row) => row.available).length,
        matureMatchedCampaigns: rows.filter((row) => row.available && row.matchedDays >= OP_MIN_DAYS).length,
        reasons,
        metrics,
      },
      rows,
      selected: selectedRow,
      scope,
    };
  }
  function previousRunsCsv(result, mode = 'campaigns') {
    if (result.errors.length) return '';
    const previous = result.previousRuns,
      selected = mode === 'campaign' ? (previous.selected ? [previous.selected] : []) : previous.rows,
      ids = Object.keys(PREVIOUS_METRICS),
      header = [
        'current_campaign_id',
        'current_campaign',
        'previous_campaign_id',
        'previous_campaign',
        'match_basis',
        'identity_key',
        'available',
        'reason',
        'launch_age_days',
        'matched_days',
        'truncated_by_previous_duration',
        'current_duration_days',
        'previous_duration_days',
        'current_start',
        'current_end',
        'current_window_start',
        'current_window_end',
        'previous_start',
        'previous_end',
        'previous_window_start',
        'previous_window_end',
        'current_impression_days',
        'current_view_days',
        'current_sales_days',
        'previous_impression_days',
        'previous_view_days',
        'previous_sales_days',
        'website_market',
        'as_of',
        'observation_cutoff',
        'history_start',
        'active_filters',
      ];
    for (const id of ids)
      header.push(
        id + '_current',
        id + '_previous',
        id + '_difference',
        id + '_relative_change',
        id + '_available',
        id + '_reason',
      );
    return [
      header,
      ...selected.map((row) => {
        const values = [
          row.currentId,
          row.name,
          row.priorId,
          row.priorName,
          row.matchBasis,
          row.identityKey,
          row.available,
          row.reason,
          row.ageDays,
          row.matchedDays,
          row.truncated,
          row.currentDurationDays,
          row.priorDurationDays,
          row.current.startDate,
          row.current.endDate,
          row.current.windowStart,
          row.current.windowEnd,
          row.prior?.startDate,
          row.prior?.endDate,
          row.prior?.windowStart,
          row.prior?.windowEnd,
          row.current.coverage.impressions,
          row.current.coverage.views,
          row.current.coverage.sales,
          row.prior?.coverage.impressions,
          row.prior?.coverage.views,
          row.prior?.coverage.sales,
          previous.scope.market,
          previous.scope.asOf,
          previous.scope.cutoff,
          previous.scope.historyStart,
          JSON.stringify(result.filters),
        ];
        for (const id of ids) {
          const metric = row.metrics[id];
          values.push(
            metric.current,
            metric.prior,
            metric.difference,
            metric.changePct,
            metric.available,
            metric.reason,
          );
        }
        return values;
      }),
    ]
      .map((row) => row.map((value) => csvCell(value)).join(','))
      .join('\r\n');
  }

  // Dropdown availability is separate from the benchmark universe. Campaign/review/status
  // selections may constrain menus without ever changing peer construction in operational().
  function campaignOptionRows(snapshot, input, omit) {
    const f = opFilters({ ...input, [omit]: omit === 'status' ? 'all' : '' });
    const cutoff = snapshot.meta.observationCutoff || snapshot.meta.asOf;
    if (omit === 'reviewGroup') f.reviewBy = '';
    return snapshot.campaigns
      .filter((campaign) => dimensions(campaign, f))
      .map((campaign) => measure(campaign, f, cutoff))
      .filter(
        (campaign) =>
          (!f.campaign || campaign.id === f.campaign) &&
          campaign.daysObserved > 0 &&
          (!f.market ||
            (campaign.dailyByMarket?.[f.market] || []).some(
              (day) => day.date >= campaign.observedStart && day.date <= campaign.observationEnd,
            )) &&
          (f.status === 'all' || !f.status || campaign.status === f.status) &&
          inReview(campaign, f) &&
          opSearch(campaign, f.search) &&
          (!f.ageMin || days(campaign.startDate, [campaign.endDate, cutoff].sort()[0]) >= Number(f.ageMin)) &&
          (!f.ageMax || days(campaign.startDate, [campaign.endDate, cutoff].sort()[0]) <= Number(f.ageMax)),
      );
  }

  function operational(snapshot, input = {}) {
    const f = opFilters(input),
      cutoff = snapshot.meta.observationCutoff || snapshot.meta.asOf,
      invalid = errors(f, cutoff),
      definitions = snapshot.metricDefinitions || [];
    if (
      f.market &&
      (!snapshot.sourceCapabilities?.market?.supported ||
        !snapshot.sourceCapabilities.market.values?.includes(f.market))
    )
      invalid.push('This website market is not supported by the snapshot.');
    if (f.reviewBy && f.reviewGroup && !snapshot.campaigns.some((campaign) => inReview(campaign, f)))
      invalid.push('This review group is not present in the snapshot.');
    if (exclusionIds(f.weeklyExclusion).length > MAX_WEEKLY_EXCLUSIONS)
      invalid.push('Choose up to 50 campaign exclusions.');
    if (exclusionIds(f.weeklyExclusion).some((id) => !snapshot.campaigns.some((c) => c.id === id)))
      invalid.push('Some excluded campaigns are unavailable in this snapshot. Review exclusions.');
    const universe = invalid.length
        ? []
        : snapshot.campaigns
            .filter((campaign) => dimensions(campaign, f))
            .map((campaign) => measure(campaign, f, cutoff)),
      peers = universe.filter(
        (campaign) =>
          campaign.status === 'completed' &&
          campaign.isMature &&
          campaign.endDate <= cutoff &&
          campaign.daysObserved > 0 &&
          campaign.durationDays >= 28 &&
          campaign.durationDays <= 42,
      ),
      picked = universe.find(
        (campaign) => campaign.id === f.campaign && campaign.daysObserved > 0 && inReview(campaign, f),
      ),
      selectedBuilt = picked ? opRow(picked, peers, definitions, cutoff) : null,
      rows = universe
        .filter(
          (campaign) =>
            campaign.daysObserved > 0 &&
            (f.status === 'all' || campaign.status === f.status) &&
            inReview(campaign, f) &&
            opSearch(campaign, f.search) &&
            (!f.ageMin || days(campaign.startDate, [campaign.endDate, cutoff].sort()[0]) >= Number(f.ageMin)) &&
            (!f.ageMax || days(campaign.startDate, [campaign.endDate, cutoff].sort()[0]) <= Number(f.ageMax)),
        )
        .map((campaign) => opRow(campaign, peers, definitions, cutoff).row),
      gapSort = (a, b) => (a.gap ?? Infinity) - (b.gap ?? Infinity) || a.name.localeCompare(b.name);
    rows.sort(gapSort);
    const comparable = rows.filter((row) => row.comparable),
      mature = comparable.filter((row) => !row.tooEarly),
      below = mature.filter((row) => row.positions.margin_per_day === 'below'),
      qualified = below.filter((row) => row.campaign.status === 'live' && row.gap < 0),
      headlineAmount = qualified.reduce((sum, row) => sum - row.gap, 0),
      totals = rows.reduce((sum, row) => sum + (row.marginToDate || 0), 0),
      coveredMargin = comparable.reduce((sum, row) => sum + (row.marginToDate || 0), 0),
      uncomparedReasons = {},
      reasonMargins = {},
      destinationSums = new Map(),
      stageCounts = {},
      stepRates = {};
    for (const row of rows.filter((item) => !item.comparable)) {
      const reason = row.reason || 'other';
      uncomparedReasons[reason] = (uncomparedReasons[reason] || 0) + 1;
      reasonMargins[reason] = (reasonMargins[reason] || 0) + (row.marginToDate || 0);
    }
    for (const row of qualified) {
      const current = destinationSums.get(row.destination) || {
        name: row.destination,
        amount: 0,
        count: 0,
      };
      current.amount -= row.gap;
      current.count++;
      destinationSums.set(row.destination, current);
    }
    for (const definition of definitions) {
      const id = definition.id,
        count = {
          metric: id,
          below: 0,
          inLine: 0,
          above: 0,
          tooEarly: 0,
          unscored: 0,
          compared: 0,
          total: rows.length,
        };
      for (const row of rows) {
        const metric = row.metrics[id];
        if (!finite(metric.z)) count.unscored++;
        else if (row.tooEarly) count.tooEarly++;
        else {
          count.compared++;
          if (metric.positionQuartile === 'below') count.below++;
          else if (metric.positionQuartile === 'above') count.above++;
          else count.inLine++;
        }
      }
      stageCounts[id] = count;
    }
    for (const [id, numeratorKey, denominatorKey] of [
      ['impressions_to_views', 'views', 'impressions'],
      ['views_to_packages', 'packages', 'views'],
      ['margin_per_view', 'margin', 'views'],
    ]) {
      const eligible = rows.filter(
          (row) =>
            finite(row.metrics[id]?.z) &&
            finite(row.totals[numeratorKey]) &&
            finite(row.totals[denominatorKey]) &&
            row.totals[denominatorKey] > 0,
        ),
        numerator = eligible.reduce((sum, row) => sum + row.totals[numeratorKey], 0),
        denominator = eligible.reduce((sum, row) => sum + row.totals[denominatorKey], 0);
      stepRates[id] = {
        value: denominator > 0 ? numerator / denominator : null,
        numerator,
        denominator,
        campaigns: eligible.length,
      };
    }
    const topDestinations = [...destinationSums.values()]
        .sort((a, b) => b.amount - a.amount)
        .map((row) => ({
          ...row,
          share: headlineAmount > 0 ? row.amount / headlineAmount : 0,
        })),
      netGap = mature.reduce((sum, row) => sum + (row.gap || 0), 0),
      selected = selectedBuilt?.row || null;
    if (selected) {
      selected.peerCampaigns = selectedBuilt.group.peers;
      const options = {
        cutoff,
        market: f.market,
        observationFrom: f.observationFrom,
        observationTo: f.observationTo,
        zeroSale: selected.zeroSale,
      };
      selected.trajectories = {
        packages: trajectory(selected.campaign, selectedBuilt.group.peers, 'packages', options),
        margin: trajectory(selected.campaign, selectedBuilt.group.peers, 'margin', options),
      };
    }
    const history = invalid.length
        ? {
            weeks: [],
            exclusionIds: exclusionIds(f.weeklyExclusion),
            population: '',
            scope: '',
            cutoff,
          }
        : weekly(snapshot, f),
      listRows = rows
        .filter(
          (row) => f.comparability === 'all' || (f.comparability === 'uncovered' ? !row.comparable : row.comparable),
        )
        .slice();
    if (f.sort === 'margin')
      listRows.sort(
        (a, b) => (b.marginToDate ?? -Infinity) - (a.marginToDate ?? -Infinity) || a.name.localeCompare(b.name),
      );
    if (f.sort === 'name') listRows.sort((a, b) => a.name.localeCompare(b.name));
    if (f.sort === 'age') listRows.sort((a, b) => b.ageDays - a.ageDays || a.name.localeCompare(b.name));
    const result = {
      filters: f,
      errors: invalid,
      rows,
      listRows,
      attention: comparable
        .filter((row) => row.campaign.status === 'live')
        .slice()
        .sort(gapSort),
      selected,
      stageCounts,
      stepRates,
      counts: {
        total: rows.length,
        live: rows.filter((row) => row.campaign.status === 'live').length,
        compared: comparable.length,
        destinationPeers: comparable.filter((row) => row.level === 'destination').length,
        countryPeers: comparable.filter((row) => row.level === 'country').length,
        uncompared: rows.length - comparable.length,
        matureCompared: mature.length,
        tooEarly: comparable.length - mature.length,
        below: below.length,
        inLine: mature.filter((row) => row.positions.margin_per_day === 'in_line').length,
        above: mature.filter((row) => row.positions.margin_per_day === 'above').length,
        belowQualified: qualified.length,
        zeroSale: rows.filter((row) => row.zeroSale).length,
      },
      headline: {
        amount: headlineAmount,
        count: qualified.length,
        periodDays: 28,
        topDestinations,
        top2Share: headlineAmount > 0 ? topDestinations.slice(0, 2).reduce((sum, row) => sum + row.share, 0) : null,
        netGap,
        netAhead: Math.max(0, netGap),
        excludedCampaignContribution: null,
      },
      coverage: {
        total: rows.length,
        compared: comparable.length,
        share: rows.length ? comparable.length / rows.length : null,
        marginToDateAllLive: totals,
        marginToDateCompared: coveredMargin,
        marginShare: totals > 0 ? coveredMargin / totals : null,
        onCountryPeers: comparable.filter((row) => row.level === 'country').length,
        uncomparedReasons,
        reasonMargins,
      },
      rollups: {
        territory: rollup(rows, 'strategicTerritory'),
        pcm: rollup(rows, 'pcmName'),
      },
      weekly: history,
      story: weeklyStory(history),
      previousRuns: previousRuns(snapshot, f, rows, selected),
      verdict: { portfolio: [], campaign: [] },
      scope: {
        cutoff,
        observation:
          f.observationFrom || f.observationTo
            ? 'Observed ' + (f.observationFrom || 'from campaign launch') + ' to ' + (f.observationTo || cutoff)
            : 'Live campaign-to-date through ' + cutoff,
        peerRule:
          'Completed mature Flash campaigns of 28 to 42 days, same country, destination and campaign type; country peers only when the destination has fewer than five peers with sales rows. Selected completed campaign excluded.',
        minimumPeerCount: OP_MIN_PEERS,
        zeroSaleMinimumDays: OP_MIN_DAYS,
        zeroSaleMinimumViews: OP_MIN_VIEWS,
        positionRule:
          'Below peer Q1, within the middle half, or above peer Q3; fewer than seven observed days has no displayed band.',
        market: f.market || 'All website markets',
        weeklyPopulation: history.population,
        reviewRule:
          'Group review restricts current campaign rows, totals and exports only. Peer comparisons and weekly history retain the active portfolio filters.',

        normalizedLegacyPeerSettings: Boolean(
          (input.cohort && input.cohort !== 'primary') ||
          (input.peerScope && input.peerScope !== 'destination') ||
          (input.minN && String(input.minN) !== String(OP_MIN_PEERS)),
        ),
      },
    };
    result.verdict = verdict(result);
    return result;
  }
  function attention(snapshot, input = {}) {
    return operational(snapshot, input).attention;
  }
  function operationalCsv(result, mode = 'campaigns') {
    if (result.errors.length) return '';
    const selected = mode === 'campaign' ? (result.selected ? [result.selected] : []) : result.listRows,
      ids = Object.keys(result.stageCounts),
      header = [
        'rank_in_filtered_list',
        'campaign_id',
        'campaign',
        'product_bk',
        'product_mapping_basis',
        'product_offer_ids',
        'country',
        'destination',
        'strategic_territory',
        'pcm_quarter_attribution',
        'owner_salesforce_display_only',
        'campaign_type',
        'start_date',
        'end_date',
        'age_days',
        'duration_days',
        'days_observed',
        'days_left',
        'days_left_capped_28',
        'coverage_sales_days',
        'coverage_views_days',
        'coverage_impressions_days',
        'too_early',
        'missing_sales_review',
        'peer_level',
        'peer_n_sales',
        'comparable',
        'uncompared_reason',
        'weakest_step',
        'gap_signed_aud',
        'website_market',
        'cutoff',
        'active_filters_json',
      ];
    for (const id of ids)
      header.push(
        id + '_actual',
        id + '_median',
        id + '_q1',
        id + '_q3',
        id + '_n',
        id + '_position',
        id + '_z',
        id + '_sd',
      );
    const rows = [
      header,
      ...selected.map((row) => {
        const index = result.listRows.findIndex((item) => item.id === row.id);
        const values = [
          index >= 0 ? index + 1 : null,
          row.id,
          row.name,
          row.campaign.productBk || '',
          row.campaign.productMapping?.basis || '',
          (row.campaign.productMapping?.bookableOfferBks || []).join('|'),
          row.country,
          row.destination,
          row.territory,
          row.pcm,
          row.ownerSalesforce,
          row.type,
          row.startDate,
          row.endDate,
          row.ageDays,
          row.durationDays,
          row.daysObserved,
          row.daysLeft,
          Math.min(row.daysLeft, 28),
          row.coverage.sales,
          row.coverage.views,
          row.coverage.impressions,
          row.tooEarly,
          row.zeroSale,
          row.level,
          row.n,
          row.comparable,
          row.reason,
          row.weakestStep,
          row.gap,
          result.filters.market || 'All website markets',
          result.scope.cutoff,
          JSON.stringify(result.filters),
        ];
        for (const id of ids) {
          const value = row.metrics[id];
          values.push(
            value.value,
            value.median,
            value.q1,
            value.q3,
            value.n,
            value.positionQuartile,
            value.z,
            value.sd,
          );
        }
        return values;
      }),
    ];
    return rows.map((row) => row.map((value) => csvCell(value)).join(',')).join('\r\n');
  }

  function weeklyCsv(result) {
    if (result.errors.length) return '';
    const baseline = result.weekly.exclusionIds.length ? 'filtered' : 'allIn';
    const header = [
      'week_start',
      'week_end',
      'partial_week',
      'population',
      'margin_aud',
      'packages',
      'offer_views',
      'search_impressions',
      'recorded_campaign_days',
      'margin_per_campaign_day_aud',
      'observation_cutoff',
      'active_filters_json',
      'excluded_campaigns_json',
    ];
    const rows = result.weekly.weeks.map((week) => {
      const values = week[baseline];
      return [
        week.weekStart,
        week.weekEnd,
        week.partial,
        baseline === 'allIn' ? 'All Flash' : 'Flash with campaign exclusions',
        values.margin,
        values.packages,
        values.views,
        values.impressions,
        values.recordedCampaignDays,
        values.marginPerCampaignDay,
        result.weekly.cutoff,
        JSON.stringify(result.filters),
        JSON.stringify(result.weekly.excludedCampaigns || []),
      ];
    });
    return [header, ...rows].map((row) => row.map((value) => csvCell(value)).join(',')).join('\r\n');
  }

  function position(z) {
    const x = finite(z) ? 120 + (Math.max(-3, Math.min(3, z)) / 3) * 112 : null;
    return {
      x,
      start: x === null ? 120 : Math.min(120, x),
      width: x === null ? 0 : Math.abs(x - 120),
      bandX: 120 - 112 / 6,
      bandWidth: 112 / 3,
    };
  }
  return {
    campaignOptionRows,
    operational,
    operationalCsv,
    weeklyCsv,
    previousRuns,
    previousRunsCsv,
    peerGroup,
    zeroSale,
    attention,
    rollup,
    trajectory,
    weekly,
    weeklyStory,
    ownerSalesforce,
    uncomparableReason,
    verdict,
    exclusionIds,
    MAX_WEEKLY_EXCLUSIONS,
    position,
    DEFAULTS,
    filters,
    fromSearch,
    toSearch,
    derive,
    overview,
    overviewCsv,
    measure,
    stats,
    compare,
    csv,
    days,
  };
});
